import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Audio Stream Event Schema - events during audio streaming
 */
export const AudioStreamEventSchema = z.discriminatedUnion("type", [
  // Transcription delta event - partial transcription as speech is detected
  z.object({
    type: z.literal("transcription-delta"),
    text: z.string(),
    isFinal: z.boolean(),
    timestamp: z.number(),
  }),
  // VAD event - voice activity detection state changes
  z.object({
    type: z.literal("vad-event"),
    state: z.enum(["speech-start", "speech-end", "silence"]),
    timestamp: z.number(),
  }),
  // Audio level event - for visual feedback
  z.object({
    type: z.literal("audio-level"),
    level: z.number(), // 0-100
    timestamp: z.number(),
  }),
  // Error event
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
  // Complete event - transcription finished
  z.object({
    type: z.literal("complete"),
    finalText: z.string(),
    duration: z.number(), // milliseconds
  }),
]);

/**
 * TTS Stream Event Schema - events during text-to-speech streaming
 */
export const TTSStreamEventSchema = z.discriminatedUnion("type", [
  // Audio chunk event - base64 encoded audio data
  z.object({
    type: z.literal("audio-chunk"),
    data: z.string(), // Base64 encoded audio
    sequence: z.number(),
    format: z.enum(["mp3", "pcm", "opus"]),
  }),
  // Text chunk event - synchronized text for highlighting
  z.object({
    type: z.literal("text-chunk"),
    text: z.string(),
    sequence: z.number(),
    startTime: z.number(), // milliseconds
    endTime: z.number(), // milliseconds
  }),
  // Complete event - TTS finished
  z.object({
    type: z.literal("complete"),
    totalChunks: z.number(),
    duration: z.number(), // milliseconds
  }),
  // Error event
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
]);

/**
 * Audio Input Configuration Schema
 */
export const AudioInputConfigSchema = z.object({
  sampleRate: z.number().default(16000),
  channels: z.number().default(1),
  enableVAD: z.boolean().default(true),
  vadSensitivity: z.enum(["low", "medium", "high"]).default("medium"),
  language: z.string().default("en"),
});

/**
 * Audio Output Configuration Schema
 */
export const AudioOutputConfigSchema = z.object({
  voice: z.string().default("Matilda"),
  model: z.string().default("eleven_flash_v2_5"),
  speed: z.number().min(0.5).max(2.0).default(1.0),
  format: z.enum(["mp3", "pcm", "opus"]).default("mp3"),
});

/**
 * Streaming Audio Input Schema
 */
export const StreamingAudioInputSchema = z.object({
  audioData: z.string(), // Base64 encoded audio chunk
  config: AudioInputConfigSchema.optional(),
  sessionId: z.string().uuid().optional(),
});

/**
 * Streaming TTS Input Schema
 */
export const StreamingTTSInputSchema = z.object({
  text: z.string(),
  config: AudioOutputConfigSchema.optional(),
  sessionId: z.string().uuid().optional(),
});

/**
 * Streaming Audio Contracts
 */
export const streamingAudioContracts = {
  /**
   * Stream audio input for real-time transcription with VAD
   * Client sends audio chunks, receives transcription events
   */
  streamAudioInput: oc
    .input(StreamingAudioInputSchema)
    .output(eventIterator(AudioStreamEventSchema))
    .route({
      method: "POST",
      path: "/ai/audio/stream-input",
    }),

  /**
   * Stream text-to-speech output
   * Client sends text, receives audio chunks
   */
  streamTextToSpeech: oc
    .input(StreamingTTSInputSchema)
    .output(eventIterator(TTSStreamEventSchema))
    .route({
      method: "POST",
      path: "/ai/audio/stream-tts",
    }),
};

// Export types
export type AudioStreamEvent = z.infer<typeof AudioStreamEventSchema>;
export type TTSStreamEvent = z.infer<typeof TTSStreamEventSchema>;
export type AudioInputConfig = z.infer<typeof AudioInputConfigSchema>;
export type AudioOutputConfig = z.infer<typeof AudioOutputConfigSchema>;
export type StreamingAudioInput = z.infer<typeof StreamingAudioInputSchema>;
export type StreamingTTSInput = z.infer<typeof StreamingTTSInputSchema>;
